import { KeyboardEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AddOrganisationModal } from '../modals/AddOrganisationModal'
import { OffboardModal } from '../modals/OffboardModal'

import {
  Box,
  Button,
  ButtonText,
  Copy,
  Flex,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { SewaOnboardingListItemEnriched } from '@percent/admin-dashboard/api/actions/sewaOnboarding/sewaOnboarding.types'
import { SewaOnboardingTableProps } from './SewaOnboardingTable.types'

export function SewaOnboardingTable({
  data,
  isLoading,
  nextPage,
  previousPage,
  totalResults,
  errorMessage,
  refresh,
  searchValue,
  onSearch,
  canOnboard,
  canOffboard
}: SewaOnboardingTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [offboardingOrg, setOffboardingOrg] = useState<SewaOnboardingListItemEnriched | undefined>()
  const [searchInput, setSearchInput] = useState(searchValue)

  const handleSearch = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch(searchInput.trim())
    }
  }

  const handleClearSearch = () => {
    setSearchInput('')
    onSearch('')
  }

  const columns = useMemo(
    () => [
      {
        id: 'organisation',
        isSortable: false,
        props: { width: '280px' },
        content: t('sewaOnboarding.table.organisation')
      },
      {
        id: 'ifscCodes',
        isSortable: false,
        props: { width: '200px' },
        content: t('sewaOnboarding.table.ifscCodes')
      },
      {
        id: 'organisationId',
        isSortable: false,
        props: { width: '200px' },
        content: t('sewaOnboarding.table.organisationId')
      },
      {
        id: 'onboardedSince',
        isSortable: false,
        props: { width: '180px' },
        content: t('sewaOnboarding.table.onboardedSince')
      },
      {
        id: 'addedBy',
        isSortable: false,
        props: { width: '160px' },
        content: t('sewaOnboarding.table.addedBy')
      },
      ...(canOffboard
        ? [{ id: 'actions', isSortable: false, props: { width: '100px' }, content: t('sewaOnboarding.table.actions') }]
        : [])
    ],
    [t, canOffboard]
  )

  const displayTotalResults = errorMessage ? 0 : totalResults ?? 0

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={displayTotalResults}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('sewaOnboarding.table.empty')}
        emptyTableDescription=""
        dataTestId="sewaOnboardingTable"
      >
        <Flex align="center" gap={16} w="100%">
          <Box flex={1}>
            <SearchInput
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              onKeyDown={handleSearch}
              handleClearValue={handleClearSearch}
              placeholder={t('sewaOnboarding.searchPlaceholder')}
              data-testid="sewa-search-input"
            />
          </Box>
          {canOnboard && (
            <Button onClick={() => setIsAddModalOpen(true)} data-testid="add-organisation-btn">
              {t('sewaOnboarding.addOrganisation')}
            </Button>
          )}
        </Flex>

        <Table>
          <TableBody>
            {data?.map(row => (
              <TableRow
                key={row.organisationId}
                onClick={handleClickAsLink(`/organizations/${row.organisationId}`, navigate)}
              >
                <TableCell>{row.organisationName}</TableCell>
                <TableCell>{row.ifscCodes.length > 0 ? row.ifscCodes.join(', ') : 'N/A'}</TableCell>
                <TableCell
                  onClick={event => {
                    event.stopPropagation()
                  }}
                >
                  <Copy text={row.organisationId}>{row.organisationId}</Copy>
                </TableCell>
                <TableCell>{getDate(row.sewa.onboardedAt)}</TableCell>
                <TableCell>{row.sewa.onboardedBy}</TableCell>
                {canOffboard && (
                  <TableCell
                    onClick={event => {
                      event.stopPropagation()
                    }}
                  >
                    <ButtonText onClick={() => setOffboardingOrg(row)}>{t('sewaOnboarding.actions.remove')}</ButtonText>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          totalResultsText={t('table.results', { count: displayTotalResults })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>

      {isAddModalOpen && <AddOrganisationModal onClose={() => setIsAddModalOpen(false)} refresh={refresh} />}

      {offboardingOrg && (
        <OffboardModal org={offboardingOrg} onClose={() => setOffboardingOrg(undefined)} refresh={refresh} />
      )}
    </>
  )
}
